import { useState, useEffect } from "react";
import {
  FileText,
  Rss,
  Download,
  CalendarDays,
  Target,
  TrendingUp,
  Plus,
  ArrowUpRight,
  Clock,
  CheckCircle2,
  Edit3,
  BarChart3,
  Users,
  Eye,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  server_post_data,
  get_dashboard_statistics,
} from "../serviceconnection/ServiceConnection";
import { showError } from "../lib/toast";

// ── Static data (unchanged) ───────────────────────────────────────────────────
const quickActions = [
  {
    label: "Create New Page",
    icon: FileText,
    path: "/pages/new",
    desc: "Add a structured template page",
  },
  {
    label: "Add Blog Post",
    icon: Rss,
    path: "/blog",
    desc: "Write and publish a blog article",
  },
  {
    label: "Add Case Study",
    icon: Download,
    path: "/case-studies",
    desc: "Write and publish a Case Study",
  },
  {
    label: "Create Event",
    icon: CalendarDays,
    path: "/webinars/add",
    desc: "Schedule a webinar or event",
  },
];

const typeColors = {
  blog: "hsl(var(--accent))",
  dynamic: "hsl(var(--info))",
  industry: "hsl(var(--info))",
  service: "hsl(var(--info))",
  product: "hsl(var(--info))",
  solution: "hsl(var(--info))",
  lead: "hsl(var(--success))",
  resource: "hsl(var(--warning))",
  event: "hsl(280 55% 55%)",
  media: "hsl(var(--muted-foreground))",
  seo: "hsl(var(--success))",
};

const statusDot = {
  published: "hsl(var(--success))",
  draft: "hsl(var(--muted-foreground))",
  scheduled: "hsl(var(--info))",
  new: "hsl(var(--accent))",
};

// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const fd = new FormData();
      const res = await server_post_data(get_dashboard_statistics, fd);
      if (res.data?.error) {
        showError("Failed to load dashboard");
        return;
      }
      setStats(res.data?.data);
      setLastUpdated(new Date());
    } catch {
      showError("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  // ── Derived metric cards from API ─────────────────────────────────────────
  const metrics = stats
    ? [
        {
          label: "Total Pages",
          value: stats.top_cards.total_pages,
          change: `+${stats.top_cards.this_month_pages} this month`,
          icon: FileText,
          gradient: "metric-pages",
          path: "/pages",
        },
        {
          label: "Blog Posts",
          value: stats.top_cards.blog_posts,
          change: `+${stats.top_cards.this_month_blogs} this month`,
          icon: Rss,
          gradient: "metric-blog",
          path: "/blog",
        },
        {
          label: "Leads Captured",
          value: stats.top_cards.leads_captured.toLocaleString(),
          change: "All time",
          icon: Target,
          gradient: "metric-leads",
          path: "/leads",
        },
        {
          label: "Upcoming Events",
          value: stats.top_cards.upcoming_events,
          change: "Active webinars",
          icon: CalendarDays,
          gradient: "metric-events",
          path: "/webinars",
        },
      ]
    : [];

  const contentSummary = stats
    ? [
        {
          label: "Total Resources",
          value: stats.content_summary.total_resources,
          icon: Download,
          path: "/dashboard",
        },
        {
          label: "Case Studies",
          value: stats.content_summary.case_studies,
          icon: BarChart3,
          path: "/case-studies",
        },
        {
          label: "Media Storage",
          value: stats.content_summary.media_storage,
          icon: Users,
          path: "/media",
        },
        {
          label: "Total Views",
          value: stats.content_summary.total_views,
          icon: Eye,
          path: "/dashboard",
        },
      ]
    : [];

  // ── Skeleton loader ───────────────────────────────────────────────────────
  const Skeleton = ({ className = "" }) => (
    <div className={`animate-pulse rounded-lg bg-muted/60 ${className}`} />
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title text-2xl">Dashboard</h1>
          <p className="page-subtitle">
            Good morning! Here's your content overview for today.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-muted-foreground">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchStats}
            disabled={loading}
            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-muted border border-border text-muted-foreground disabled:opacity-50 transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <div
            className="w-2 h-2 rounded-full animate-pulse-glow"
            style={{ background: "hsl(var(--accent))" }}
          />
        </div>
      </div>

      {/* ── Metric Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {loading
          ? Array(4)
              .fill(0)
              .map((_, i) => <Skeleton key={i} className="h-36" />)
          : metrics.map((m) => (
              <Link
                key={m.label}
                to={m.path}
                className="metric-card group cursor-pointer"
                style={{ background: `var(--${m.gradient})` }}
              >
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                      <m.icon className="w-[18px] h-[18px] text-white" />
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-white/50 group-hover:text-white/80 transition-colors" />
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">
                    {m.value}
                  </p>
                  <p className="text-sm font-medium text-white/70">{m.label}</p>
                  <p className="text-[11px] text-white/50 mt-1 flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" /> {m.change}
                  </p>
                </div>
              </Link>
            ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Quick Actions — static, no API needed */}
        <div className="section-panel">
          <div className="section-panel-header">
            <h2 className="section-panel-title flex items-center gap-2">
              <Plus
                className="w-4 h-4"
                style={{ color: "hsl(var(--accent))" }}
              />
              Quick Actions
            </h2>
          </div>
          <div className="section-panel-body space-y-2">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                to={action.path}
                className="flex items-center gap-3.5 p-3 rounded-lg border border-border hover:border-accent/40 hover:bg-accent-muted/30 transition-all duration-150 group"
              >
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: "hsl(var(--accent-muted))" }}
                >
                  <action.icon
                    className="w-4 h-4"
                    style={{ color: "hsl(var(--accent))" }}
                  />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors">
                    {action.label}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {action.desc}
                  </p>
                </div>
                <ArrowUpRight className="w-4 h-4 text-muted-foreground ml-auto flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            ))}
          </div>
        </div>

        {/* Content Summary */}
        <div className="section-panel">
          <div className="section-panel-header">
            <h2 className="section-panel-title flex items-center gap-2">
              <BarChart3
                className="w-4 h-4"
                style={{ color: "hsl(var(--accent))" }}
              />
              Content Summary
            </h2>
          </div>
          <div className="section-panel-body grid grid-cols-2 gap-3">
            {loading
              ? Array(4)
                  .fill(0)
                  .map((_, i) => <Skeleton key={i} className="h-24" />)
              : contentSummary.map((s) => (
                  <Link
                    key={s.label}
                    to={s.path}
                    className="cms-card p-4 group hover:border-accent/30 cursor-pointer"
                  >
                    <s.icon
                      className="w-5 h-5 mb-3"
                      style={{ color: "hsl(var(--accent))" }}
                    />
                    <p className="text-2xl font-bold text-foreground">
                      {s.value}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {s.label}
                    </p>
                  </Link>
                ))}

            {/* SEO Warning — dynamic */}
            {!loading && stats?.missing_seo_count > 0 && (
              <div
                className="col-span-2 p-3 rounded-lg flex items-center gap-2.5"
                style={{ background: "hsl(var(--warning-muted))" }}
              >
                <AlertCircle
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: "hsl(var(--warning))" }}
                />
                <p
                  className="text-xs font-medium"
                  style={{ color: "hsl(var(--warning))" }}
                >
                  {stats.missing_seo_count} page
                  {stats.missing_seo_count > 1 ? "s" : ""} have missing SEO
                  metadata
                </p>
              </div>
            )}
          </div>
        </div>

        {/* System Health */}
        <div className="section-panel">
          <div className="section-panel-header">
            <h2 className="section-panel-title flex items-center gap-2">
              <CheckCircle2
                className="w-4 h-4"
                style={{ color: "hsl(var(--success))" }}
              />
              System Status
            </h2>
            <span className="status-published">All Systems Operational</span>
          </div>
          <div className="section-panel-body space-y-3">
            {loading
              ? Array(5)
                  .fill(0)
                  .map((_, i) => <Skeleton key={i} className="h-8" />)
              : (stats?.system_health ?? []).map((s) => (
                  <div
                    key={s.name}
                    className="flex items-center justify-between py-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2 h-2 rounded-full"
                        style={{ background: "hsl(var(--success))" }}
                      />
                      <span className="text-sm text-foreground">{s.name}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      {s.latency !== "—" && (
                        <span className="text-xs text-muted-foreground">
                          {s.latency}
                        </span>
                      )}
                      <span className="status-published">{s.status}</span>
                    </div>
                  </div>
                ))}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="section-panel">
        <div className="section-panel-header">
          <h2 className="section-panel-title flex items-center gap-2">
            <Clock
              className="w-4 h-4"
              style={{ color: "hsl(var(--accent))" }}
            />
            Recent Activity
          </h2>
          <button
            className="text-xs font-semibold hover:underline"
            style={{ color: "hsl(var(--accent))" }}
          >
            View all
          </button>
        </div>
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-6 space-y-3">
              {Array(5)
                .fill(0)
                .map((_, i) => (
                  <Skeleton key={i} className="h-10" />
                ))}
            </div>
          ) : (
            <table className="w-full cms-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Content Item</th>
                  <th>Type</th>
                  <th>By</th>
                  <th>Status</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recent_activity ?? []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center py-10 text-muted-foreground text-sm"
                    >
                      No recent activity
                    </td>
                  </tr>
                ) : (
                  (stats?.recent_activity ?? []).map((a, i) => (
                    <tr key={i} className="group">
                      <td>
                        <span className="inline-flex items-center gap-1.5">
                          <Edit3 className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="font-medium">{a.action}</span>
                        </span>
                      </td>
                      <td>
                        <span className="text-foreground font-medium line-clamp-1">
                          {a.item}
                        </span>
                      </td>
                      <td>
                        <span
                          className="text-xs font-semibold px-2 py-1 rounded-full capitalize"
                          style={{
                            background: `${typeColors[a.type] ?? "hsl(var(--muted))"}20`,
                            color:
                              typeColors[a.type] ??
                              "hsl(var(--muted-foreground))",
                          }}
                        >
                          {a.type}
                        </span>
                      </td>
                      <td className="text-muted-foreground">{a.user}</td>
                      <td>
                        <span
                          className={`status-${a.status} inline-flex items-center gap-1`}
                        >
                          <span
                            className="w-1.5 h-1.5 rounded-full"
                            style={{
                              background:
                                statusDot[a.status] ??
                                "hsl(var(--muted-foreground))",
                            }}
                          />
                          {a.status}
                        </span>
                      </td>
                      <td className="text-muted-foreground text-xs whitespace-nowrap">
                        {a.time_ago}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
